import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { refreshAccessToken } from 'features/auth/services/auth-service';
import klaxLogo from 'assets/images/Vector.svg';
import { useTranslation } from 'react-i18next';
import { usePendingSeatSettlement } from 'features/subscription/hooks/use-pending-seats';
import { readPendingSeats } from 'features/subscription/utils/pending-seats';
import { ResultCard, TopUpResult } from 'features/credits/components/topup-result';
import { useTopUpByPayment } from 'features/credits/hooks/use-credits';

/**
 * One result URL serves every payment. A payment the credits server knows is a top-up and is shown
 * from the server's word; any other payment (404) is a seat payment and keeps the old behaviour.
 */
export function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const paymentDetailId = searchParams.get('paymentDetailId');
    return paymentDetailId ? <PaymentLookup paymentDetailId={paymentDetailId} /> : <SeatPaymentResult />;
}

function PaymentLookup({ paymentDetailId }: { paymentDetailId: string }) {
    const { t } = useTranslation();
    const { data: topUp, isLoading, isError, refetch } = useTopUpByPayment(paymentDetailId);

    if (isLoading) {
        return <ResultCard tone="waiting" title={t('CONFIRMING_PAYMENT', 'Confirming your payment...')} />;
    }
    if (isError) {
        return (
            <ResultCard
                tone="pending"
                title={t('PAYMENT_LOOKUP_FAILED', 'We could not check your payment yet.')}
                description={t('PAYMENT_LOOKUP_FAILED_HINT', 'Check again in a moment.')}
            >
                <Button className="w-full sm:w-auto" onClick={() => refetch()}>
                    {t('TRY_AGAIN', 'Try Again')}
                </Button>
            </ResultCard>
        );
    }
    return topUp ? <TopUpResult topUp={topUp} /> : <SeatPaymentResult />;
}

function SeatPaymentResult() {
    const [searchParams] = useSearchParams();
    const paymentDetailId = searchParams.get('paymentDetailId');
    const status = searchParams.get('status');
    // Only 'success' is a paid payment. 'cancelled' is redirected below; anything else is not confirmed yet.
    const isSuccess = status === 'success';
    const isFailed = status === 'fail' || status === 'failed';
    const isPending = !isSuccess && !isFailed;
    const { theme } = useTheme();
    const { configurationSettings } = useConfig();
    const { t } = useTranslation();
    // Read before the seats are settled: a payment for seats nobody was promised leaves them free.
    const [givesSeats] = useState(() => readPendingSeats().length > 0);
    const { waiting } = usePendingSeatSettlement();

    // Refresh access token when payment is successful to get updated permissions
    useEffect(() => {
        const refreshTokenAfterPayment = async () => {
            if (configurationSettings && isSuccess) {
                try {
                    await refreshAccessToken(configurationSettings);
                } catch (error) {
                    console.warn('Failed to refresh access token after successful payment:', error);
                    // Don't block the user experience if token refresh fails
                }
            }
        };

        refreshTokenAfterPayment();
    }, [configurationSettings, isSuccess]);

    if (status === 'cancelled') {
        return <Navigate to="/payment-cancelled" replace />;
    }

    const handleButtonClick = () => {
        if (isFailed || isPending || !givesSeats) {
            window.location.href = '/identity-management';
        } else {
            // Reload the window to ensure all stores are refreshed with updated user data
            window.location.href = '/chat';
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4 w-full">
            <div className="max-w-2xl w-full text-center space-y-8">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img
                        src={klaxLogo}
                        alt="KLAX!"
                        className={`h-auto w-64 object-contain ${theme === 'dark' ? '' : 'drop-shadow-[0_4px_8px_rgba(0,0,0,0.6)]'}`}
                    />
                </div>

                {/* Status Icon */}
                <div className="flex justify-center">
                    <div className="w-[334px] h-[334px] flex items-center justify-center">
                        {isFailed && <XCircle className="w-48 h-48 text-destructive" strokeWidth={1} />}
                        {isPending && <Clock className="w-48 h-48 text-muted-foreground" strokeWidth={1} />}
                        {isSuccess && <CheckCircle className="w-48 h-48 text-success-green" strokeWidth={1} />}
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    <div className="space-y-4">
                        {/* Title */}
                        <h1 className="text-3xl font-bold leading-[48px] text-foreground font-sans">
                            {isFailed && t('PAYMENT_FAILED', 'Payment failed')}
                            {isPending && t('PAYMENT_PENDING', 'Payment pending')}
                            {isSuccess && t('PAYMENT_SUCCESSFUL')}
                        </h1>

                        {paymentDetailId && (
                            <div className="flex items-center justify-center gap-2">
                                <span className="font-semibold text-foreground">
                                    {t('PAYMENT_DETAIL_ID')}:
                                </span>
                                <span className="text-xl font-normal leading-[30px] text-muted-foreground font-sans">
                                    {paymentDetailId}
                                </span>
                            </div>
                        )}

                        {/* Description */}
                        {isPending && (
                            <p className="text-xl font-normal leading-[30px] text-muted-foreground font-sans">
                                {t(
                                    'PAYMENT_PENDING_DESCRIPTION',
                                    'We have not received confirmation of your payment yet. If you cancelled, no charge was made. If you paid, it can take a minute to confirm.'
                                )}
                            </p>
                        )}
                        {isSuccess && (
                            <p className="text-xl font-normal leading-[30px] text-muted-foreground font-sans">
                                {givesSeats
                                    ? t('THANK_YOU_FOR_YOUR_SUBSCRIPTION')
                                    : t(
                                          'SEATS_PAID_NOT_GIVEN',
                                          'Your seats are paid. Nobody holds them yet: give them to people from the Plan column in Identity Management.'
                                      )}
                            </p>
                        )}
                        {!isFailed && waiting > 0 && (
                            <p className="text-sm text-muted-foreground">
                                {t('SEATS_BEING_GIVEN', 'Giving out the seats as soon as the payment is confirmed...')}
                            </p>
                        )}
                    </div>

                    {/* Button */}
                    <div className="flex justify-center">
                        <Button
                            onClick={handleButtonClick}
                            className="w-[330px] h-[46px] bg-primary hover:bg-primary-600 font-semibold text-[15px] leading-[26px] rounded-md px-4 py-2.5 transition-colors"
                        >
                            {isFailed || isPending
                                ? t('TRY_AGAIN', 'Try Again')
                                : givesSeats
                                  ? t('START_USING_APP')
                                  : t('GIVE_THE_SEATS', 'Give the seats')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}